import { useState, useRef, useEffect } from "react";
import './App.css';
import { BACKGROUND_MUSIC } from "./components/constants";
import CharacterSelection from "./components/CharacterSelection";
import ChatScreen from "./components/ChatScreen";
import BuyCredits from "./components/BuyCredits";
import PaymentSuccess from "./components/PaymentSuccess";
import LumiApp from "./components/lumi/LumiApp";
import LearningTowersPreview from "./components/LearningTowersPreview";
import PoodleTalkAuthCallback from "./components/poodletalk/PoodleTalkAuthCallback";
import PrivacyPolicy from "./components/PrivacyPolicy";
import OrganisationSignIn from "./components/poodletalk/OrganisationSignIn";
import HotelSignUp from "./components/poodletalk/HotelSignUp";
import HotelActivation from "./components/poodletalk/HotelActivation";
import HotelCredits from "./components/poodletalk/HotelCredits";
import VerifyApprovedLanding from "./components/poodletalk/VerifyApprovedLanding";
import ComingSoonGate from "./components/poodletalk/ComingSoonGate";
import HotelUsageReport from "./components/poodletalk/HotelUsageReport";
import { isHotelModeActive, validateHotel } from "./components/poodletalk/hotelClient";

function App() {
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [ageMode, setAgeMode] = useState('kids');
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [currentSong, setCurrentSong] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState('shimmer');
  const [autoRead, setAutoRead] = useState(false);
  const [bigText, setBigText] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [activateSector, setActivateSector] = useState('hotel');
  // Auth callback as proper state (not a derived const) so we can actually
  // unmount the bouncing-poodle screen when sign-in completes. Previously
  // we recomputed isPtAuthCallback from window.location.hash on every render,
  // but setCurrentPage('home') was a no-op (currentPage was already 'home'),
  // so no re-render happened and the user got stuck on the spinner.
  const [showAuthCallback, setShowAuthCallback] = useState(() =>
    typeof window !== "undefined" &&
    window.location.hash?.includes("session_id=") &&
    !window.location.pathname.startsWith("/lumi")
  );
  const audioRef = useRef(null);

  // Detect family-account Google auth callback (#session_id=…) on root path.
  // Lumi has its own callback inside LumiApp (gated to /lumi), so we ONLY
  // catch this here when the URL is the family app, not /lumi.
  const isPtAuthCallback = showAuthCallback;

  // Simple route handling based on URL path
  useEffect(() => {
    const path = window.location.pathname;
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const sectorParam = params.get('sector');
    if (sectorParam && ['hotel', 'school', 'charity'].includes(sectorParam)) {
      setActivateSector(sectorParam);
    }
    if (path === '/buy') {
      setCurrentPage('buy');
    } else if (path === '/lumi' || path.startsWith('/lumi')) {
      setCurrentPage('lumi');
    } else if (path === '/towers-preview') {
      setCurrentPage('towers-preview');
    } else if (path === '/privacy') {
      setCurrentPage('privacy');
    } else if (path === '/organisations') {
      setCurrentPage('organisations');
    } else if (path === '/verify/approved') {
      setCurrentPage('verify-approved');
    } else if (path === '/hotel/activate') {
      setCurrentPage('hotel-activate');
    } else if (path === '/hotel/signup') {
      setCurrentPage('hotel-signup');
    } else if (path === '/school/signup') {
      setCurrentPage('school-signup');
    } else if (path === '/charity/signup') {
      setCurrentPage('charity-signup');
    } else if (path === '/hotel/credits') {
      setCurrentPage('hotel-credits');
    } else if (path === '/hotel/usage-report') {
      setCurrentPage('hotel-usage-report');
    } else if (path === '/hotel/thank-you' || path === '/success' || search.includes('session_id=')) {
      setCurrentPage('success');
    } else {
      setCurrentPage('home');
    }
    // Re-validate hotel session on every app open (per requirement).
    validateHotel();
  }, []);

  const handleSelectCharacter = (character) => {
    setSelectedCharacter(character);
  };

  const handleBack = () => {
    setSelectedCharacter(null);
    if (audioRef.current) {
      audioRef.current.pause();
      setIsMusicPlaying(false);
    }
  };

  const navigateTo = (page, opts) => {
    const routes = {
      buy: '/buy',
      organisations: '/organisations',
      'hotel-activate': '/hotel/activate',
      'hotel-signup': '/hotel/signup',
      'school-signup': '/school/signup',
      'charity-signup': '/charity/signup',
      'hotel-credits': '/hotel/credits',
      'hotel-usage-report': '/hotel/usage-report',
    };
    const hash = opts && opts.hash ? `#${opts.hash}` : '';
    const query = opts && opts.query ? `?${opts.query}` : '';
    if (page === 'buy') {
      // In Hotel Mode "Get Chat Credits" routes to Hotel Membership & Credits instead
      if (isHotelModeActive()) {
        window.history.pushState({}, '', '/hotel/credits');
        setCurrentPage('hotel-credits');
        return;
      }
      window.history.pushState({}, '', '/buy');
      setCurrentPage('buy');
      return;
    }
    if (routes[page]) {
      window.history.pushState({}, '', routes[page] + query + hash);
      setCurrentPage(page);
      return;
    }
    window.history.pushState({}, '', '/');
    setCurrentPage('home');
    setSelectedCharacter(null);
  };

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      const params = new URLSearchParams(search);
      const sectorParam = params.get('sector');
      if (sectorParam && ['hotel', 'school', 'charity'].includes(sectorParam)) {
        setActivateSector(sectorParam);
      }
      if (path === '/buy') {
        setCurrentPage('buy');
      } else if (path === '/lumi' || path.startsWith('/lumi')) {
        setCurrentPage('lumi');
      } else if (path === '/towers-preview') {
        setCurrentPage('towers-preview');
      } else if (path === '/privacy') {
        setCurrentPage('privacy');
      } else if (path === '/organisations') {
        setCurrentPage('organisations');
      } else if (path === '/verify/approved') {
        setCurrentPage('verify-approved');
      } else if (path === '/hotel/activate') {
        setCurrentPage('hotel-activate');
      } else if (path === '/hotel/signup') {
        setCurrentPage('hotel-signup');
      } else if (path === '/school/signup') {
        setCurrentPage('school-signup');
      } else if (path === '/charity/signup') {
        setCurrentPage('charity-signup');
      } else if (path === '/hotel/credits') {
        setCurrentPage('hotel-credits');
      } else if (path === '/hotel/thank-you' || path === '/success' || search.includes('session_id=')) {
        setCurrentPage('success');
      } else {
        setCurrentPage('home');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (currentPage === 'lumi') {
    return <LumiApp />;
  }

  // Family-account Google auth callback (fragment is #session_id=…)
  if (isPtAuthCallback) {
    return (
      <PoodleTalkAuthCallback
        onDone={() => {
          // Notify any header pills to refresh, drop the callback screen,
          // and ensure we land on home.
          window.dispatchEvent(new CustomEvent("pt_auth_changed"));
          setShowAuthCallback(false);
          setCurrentPage('home');
        }}
      />
    );
  }

  if (currentPage === 'towers-preview') {
    return <LearningTowersPreview />;
  }

  if (currentPage === 'privacy') {
    return <PrivacyPolicy />;
  }

  if (currentPage === 'buy') {
    return <BuyCredits onBack={() => navigateTo('home')} onGoOrganisations={() => navigateTo('organisations')} />;
  }

  if (currentPage === 'verify-approved') {
    return <ComingSoonGate><VerifyApprovedLanding /></ComingSoonGate>;
  }

  if (currentPage === 'organisations') {
    return (
      <ComingSoonGate>
        <OrganisationSignIn
          onBack={() => navigateTo('home')}
          onGoHotelSignup={(target) => navigateTo('hotel-signup', target === 'addons' ? { hash: 'addons' } : undefined)}
          onGoSectorSignup={(sec, target) => navigateTo(`${sec}-signup`, target === 'addons' ? { hash: 'addons' } : undefined)}
          onGoHotelActivate={(sec) => {
            const s = ['hotel', 'school', 'charity'].includes(sec) ? sec : 'hotel';
            setActivateSector(s);
            navigateTo('hotel-activate', { query: `sector=${s}` });
          }}
          onGoHotelCredits={() => navigateTo('hotel-credits')}
          onGoUsageReport={() => navigateTo('hotel-usage-report')}
        />
      </ComingSoonGate>
    );
  }

  if (currentPage === 'hotel-signup') {
    return <ComingSoonGate><HotelSignUp sector="hotel" onBack={() => navigateTo('organisations')} /></ComingSoonGate>;
  }

  if (currentPage === 'school-signup') {
    return <ComingSoonGate><HotelSignUp sector="school" onBack={() => navigateTo('organisations')} /></ComingSoonGate>;
  }

  if (currentPage === 'charity-signup') {
    return <ComingSoonGate><HotelSignUp sector="charity" onBack={() => navigateTo('organisations')} /></ComingSoonGate>;
  }

  if (currentPage === 'hotel-activate') {
    return <HotelActivation sector={activateSector} onBack={() => navigateTo('organisations')} onActivated={() => navigateTo('home')} />;
  }

  if (currentPage === 'hotel-credits') {
    return <HotelCredits onBack={() => navigateTo('home')} onGoSignup={() => navigateTo('hotel-signup')} />;
  }

  if (currentPage === 'hotel-usage-report') {
    return <HotelUsageReport onBack={() => navigateTo('home')} />;
  }

  if (currentPage === 'success') {
    return <PaymentSuccess onBack={() => navigateTo('home')} />;
  }

  return (
    <div className="App">
      <audio
        ref={audioRef}
        src={BACKGROUND_MUSIC[currentSong]?.url}
        loop
        preload="auto"
      />
      
      {selectedCharacter ? (
        <ChatScreen 
          character={selectedCharacter}
          onBack={handleBack}
          isMusicPlaying={isMusicPlaying}
          setIsMusicPlaying={setIsMusicPlaying}
          audioRef={audioRef}
          currentSong={currentSong}
          setCurrentSong={setCurrentSong}
          selectedVoice={selectedVoice}
          setSelectedVoice={setSelectedVoice}
          autoRead={autoRead}
          setAutoRead={setAutoRead}
          bigText={bigText}
          setBigText={setBigText}
          ageMode={ageMode}
          setAgeMode={setAgeMode}
          onNavigate={navigateTo}
        />
      ) : (
        <CharacterSelection 
          onSelect={handleSelectCharacter}
          ageMode={ageMode}
          setAgeMode={setAgeMode}
          onNavigate={navigateTo}
        />
      )}
    </div>
  );
}

export default App;
